import {
    mapBnefTrackingDimensionToMatomo,
    MATOMO_USER_CUSTOM_DIMENSION_MAP,
} from '@/services/Analytics/bnefMatomoConfiguration';
import SessionStorage from '@/services/SessionStorage';
import { isBrowser } from '@/services/Utilities/Runtime';
import { getUrlFromCurrentView } from '@/utils/siteUtils';
import type { Environment } from '@bbnpm/bb-analytics';
import {
    BloombergAnalyticsProvider,
    createBrowserHistoryPageTracker,
    createEventTracker,
    deInitialize,
    initialize,
    setUserId,
} from '@bbnpm/bb-analytics';
import merge from 'deepmerge';
import { setupAutomaticEventTrackers } from './bbAnalyticsTrackers';
import type {
    AnalyticsPageDataObject,
    IBaseAnalyticsData,
    IExternalReferrerData,
    InitialTrackingDimensions,
    PageContextDimensionValues,
} from './bnefAnalyticsInterfaces';
import type { SiteSearchCategory, TrackingAction } from './bnefAnalyticsSettings';
import { TrackingCategory } from './bnefAnalyticsSettings';

const EMAIL_REFERRER_PARAMETER = 'e';
const PREVIOUS_PAGE_STORAGE_KEY = ['bbanalytics', 'last_page'];
const DEFAULT_USER_LOCALE = 'en';

export let activeAnalyticsConfiguration: IAnalyticsConfiguration = null;
export let analyticsConfigurationInitialized = false;
export let analyticsDebugModeEnabled = false;
let activeBaseAnalyticsData: IBaseAnalyticsData = null;
let bbAnalyticsEventTracker: ReturnType<typeof createEventTracker> = null;
let bbAnalyticsPageHistoryTracker: ReturnType<typeof createBrowserHistoryPageTracker> = null;

export interface IAnalyticsConfiguration {
    debug: boolean;
    bbAnalyticsMatomoSiteId: number;
    bbAnalyticsMatomoEnvId: Environment;
}

export const prepareBBAnalytics = (
    analyticsConfiguration: IAnalyticsConfiguration,
    baseAnalyticsData: IBaseAnalyticsData
) => {
    if (!isBrowser) {
        return;
    }
    if (!baseAnalyticsData) {
        console.info(
            '[BBAnalytics] No base metadata available for provider on current page. Tracking using default values.'
        );
    }

    activeAnalyticsConfiguration = analyticsConfiguration;
    activeBaseAnalyticsData = baseAnalyticsData;
    refreshBBAnalyticsProviders();
};

export const trackEvent = (
    action: TrackingAction,
    category: TrackingCategory = null,
    label: string = null,
    value: number = null
) => {
    if (!analyticsConfigurationInitialized) {
        console.info('[BBAnalytics] Provider not initialized. Tracking unavailable.');
        return;
    }
    const trackingData = prepareTrackingData(action, category, label, value);
    if (analyticsDebugModeEnabled) {
        console.info('[BBAnalytics] Tracking event', trackingData);
    }
    bbAnalyticsEventTracker(trackingData);
};

export const trackPageLoad = () => {
    if (!analyticsConfigurationInitialized) {
        console.info('[BBAnalytics] Provider not initialized. Tracking unavailable.');
        return;
    }
    bbAnalyticsPageHistoryTracker(window.location);
};

export const prepareTrackingData = (
    action: TrackingAction,
    category: TrackingCategory = null,
    label: string = null,
    value: number = null
) => {
    return {
        action,
        category: category ?? TrackingCategory.General,
        ...(label && { label }),
        ...(value !== null && { value }),
    };
};

export const trackSiteSearch = (keyword: string, category: SiteSearchCategory) => {
    if (!activeAnalyticsConfiguration || !analyticsConfigurationInitialized) {
        console.info('[BBAnalytics] Provider not initialized. Site search tracking unavailable.');
        return;
    }

    const provider = getBloombergAnalyticsProvider(activeAnalyticsConfiguration);
    provider.trackSiteSearch({
        keyword,
        category,
    });
};

/**
 * Useful in client-side routing when only some dimensions have changed.
 */
export const refreshBBAnalyticsProviders = (updatedAnalyticsData?: IBaseAnalyticsData) => {
    if (!activeBaseAnalyticsData || !activeAnalyticsConfiguration) {
        console.warn('[BBAnalytics] Providers cannot be refreshed before initialization.');
        return;
    }

    analyticsConfigurationInitialized = false;
    analyticsDebugModeEnabled = activeAnalyticsConfiguration.debug;

    const initialDimensionValues = getInitialMetadataForProvider(
        merge(activeBaseAnalyticsData, updatedAnalyticsData ?? {})
    );

    const provider = getBloombergAnalyticsProvider(activeAnalyticsConfiguration);
    deInitialize();
    initialize({
        provider,
        options: {
            debug: analyticsDebugModeEnabled,
            debugWarnOnly: true,
        },
        dimensions: mapBnefTrackingDimensionToMatomo(initialDimensionValues),
    });
    setUserId(provider, initialDimensionValues.userId);

    bbAnalyticsEventTracker = createEventTracker();
    bbAnalyticsPageHistoryTracker = createBrowserHistoryPageTracker({ provider, includeQuery: true });

    setupAutomaticEventTrackers();
    analyticsConfigurationInitialized = true;

    if (analyticsDebugModeEnabled) {
        console.info('[BBAnalytics] Provider Ready', { initialDimensionValues });
    }
};

let bloombergAnalyticsProvider: BloombergAnalyticsProvider = null;
const getBloombergAnalyticsProvider = ({
    bbAnalyticsMatomoEnvId,
    bbAnalyticsMatomoSiteId,
}: IAnalyticsConfiguration) => {
    if (bloombergAnalyticsProvider) {
        return bloombergAnalyticsProvider;
    }
    if (analyticsDebugModeEnabled) {
        console.info('Preparing Bloomberg Analytics Provider', { bbAnalyticsMatomoSiteId, bbAnalyticsMatomoEnvId });
    }
    // Cache the provider to avoid necessarily rebuilding it when doing client-side rendering.
    bloombergAnalyticsProvider = new BloombergAnalyticsProvider({
        siteId: bbAnalyticsMatomoSiteId,
        environment: bbAnalyticsMatomoEnvId,
        internal: false,
        userCustomDimensionsMap: MATOMO_USER_CUSTOM_DIMENSION_MAP,
        routeToBlpEndpoint: true, // logs.blpprofessional.com isn't blocked in China, instead of logs.bloomberg.com
    });
    // By our TOS, we can use consented tracking by default
    bloombergAnalyticsProvider.setIsConsented();
    return bloombergAnalyticsProvider;
};

/**
 * Return the values for the initial page metadata used by the Analytics provider.
 * This uses the bb-analytics defined dimension names, which will be translated into the corresponding backend variables.
 */
const getInitialMetadataForProvider = (baseAnalyticsData: IBaseAnalyticsData): InitialTrackingDimensions => {
    const currentUrlData = getUrlFromCurrentView();

    const currentPageData = baseAnalyticsData?.page ?? null;
    const externalReferrerData = getExternalReferrerData(currentUrlData);
    const previousPageData = getPreviousPageMetadata();
    const currentUserData = baseAnalyticsData?.user ?? null;
    updateLatestPageMetadata(currentPageData);

    const currPageName = currentPageData?.pageInfo?.pageName ?? `Undefined Page: ${currentUrlData.href}`;
    const currPageSection = currentPageData?.category?.siteSection ?? 'Undefined Site Section';
    const currUserId = currentUserData?.id.toString() ?? '0';

    const pageContextValues: PageContextDimensionValues = {
        site: currentUrlData.hostname,
        siteSection: currPageSection,
        siteSubSectionLevel1: currentPageData?.category?.subSection1 ?? null,
        siteSubSectionLevel2: currentPageData?.category?.subSection2 ?? null,

        bnefPageName: currPageName,
        bnefPreviousPageName: previousPageData?.pageInfo?.pageName ?? 'No Previous Page',

        userSiteLanguage: currentUserData?.l10n ?? DEFAULT_USER_LOCALE,
        userId: currUserId,
        audience: 'web',
        colorScheme: window.matchMedia('(prefers-color-scheme:dark)').matches ? 'dark' : 'light',
    };

    // Include the following properties only if available.
    if (currentPageData?.content) {
        pageContextValues.contentId = currentPageData.content.id;
        pageContextValues.contentType = currentPageData.content.type;
    }
    if (externalReferrerData) {
        pageContextValues.externalReferrerPlatform = externalReferrerData.platform;
        pageContextValues.externalReferrerSource = externalReferrerData.source;
    }
    if (currentUserData?.hasSetInterests !== undefined) {
        pageContextValues.userHasInterests = currentUserData.hasSetInterests.toString();
    }
    if (currentUserData?.type) {
        pageContextValues.userType = currentUserData?.type;
    }

    return pageContextValues;
};

/**
 * Referrer data for when views are received via external platforms.
 */
const getExternalReferrerData = (currentUrlData: URL) => {
    const referrer = currentUrlData.searchParams.get(EMAIL_REFERRER_PARAMETER);
    if (!referrer) {
        return null;
    }
    const parts = referrer.split(':');
    return {
        source: parts[0],
        platform: parts[1],
    } as IExternalReferrerData;
};

/**
 * Helpers to maintain and store previous page metadata.
 */
const getPreviousPageMetadata = () => {
    return SessionStorage.get<AnalyticsPageDataObject>(PREVIOUS_PAGE_STORAGE_KEY) ?? null;
};
const updateLatestPageMetadata = (pageMetadata: AnalyticsPageDataObject | null) => {
    if (!pageMetadata) {
        return;
    }
    SessionStorage.set(PREVIOUS_PAGE_STORAGE_KEY, pageMetadata);
};
